import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { ClassImportListType, StudentImportListType } from "utils/interfaces";
import ProcessingDataAnimation from "./utils/ProcessingDataAnimation";
import ConflictButton from "./utils/ConflictButton";
import { dateDDMMYYYYRegex } from "utils/regex";
import { genderList } from "config/optionValues";
import { capitaliseFirstChar } from "utils/utils";
import { MainContext } from "components/utils/Contexts";

interface CheckFormatProps {
  file: string[];
  checkFormatState: "import" | "processing" | "warning" | "missing-fields";
  students: StudentImportListType[];
  comeBackToStage1: () => void;
  setStage: React.Dispatch<React.SetStateAction<number>>;
  setClasses: React.Dispatch<React.SetStateAction<ClassImportListType[]>>;
  setStudents: React.Dispatch<React.SetStateAction<StudentImportListType[]>>;
  setCsvErrors: React.Dispatch<React.SetStateAction<string[][]>>;
  setImportOption: React.Dispatch<
    React.SetStateAction<"replace" | "create" | "update">
  >;
  setIsMissingFields: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define the data structure
const DATA_STRUCTURE: {
  [key: string]: {
    name: string; // Name to display
    headerReferences: string[]; // List of names of how a field could be defined at the header
    required: boolean; // If the field is required or not
  };
} = {
  firstName: {
    name: "First Name",
    headerReferences: ["first name", "firstname"],
    required: true,
  },
  lastName: {
    name: "Last Name",
    headerReferences: ["last name", "lastname"],
    required: true,
  },
  dateOfBirth: {
    name: "DOB",
    headerReferences: ["dateofbirth", "dob", "date of birth"],
    required: false,
  },
  gender: { name: "Gender", headerReferences: ["gender"], required: false },
  classes: {
    name: "Class Name",
    headerReferences: ["class", "classes", "class name", "class names"],
    required: true,
  },
  heritage: {
    name: "Aboriginal or Torres Strait Islander origin",
    headerReferences: [
      "heritage",
      "aboriginal or torres strait islander origin",
      "aboriginal or torres strait islander origin (y/n)",
    ],
    required: false,
  },
  username: {
    name: "User Name",
    headerReferences: ["user name", "username", "user"],
    required: false,
  },
  notes: {
    name: "Notes",
    headerReferences: ["notes", "note"],
    required: false,
  },
};

const CheckFormat: React.FC<CheckFormatProps> = ({
  file,
  checkFormatState,
  students,
  comeBackToStage1,
  setStage,
  setClasses,
  setStudents,
  setCsvErrors,
  setImportOption,
  setIsMissingFields,
}) => {
  // States definition
  const [header, setHeader] = useState<string[]>([]);
  const [body, setBody] = useState<string[][]>([]);
  const [numClasses, setNumClasses] = useState(0);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // Get the values from the context
  const { contextSchoolByRole } = useContext(MainContext);
  /**
   * Function to set the missing fields boolean if missing
   */
  useEffect(() => {
    if (missingFields) {
      setIsMissingFields(!!missingFields.length);
    }
  }, [missingFields]);

  /**
   * Function used to format the data for the table
   */
  useEffect(() => {
    // Check again if the file exists, otherwise come back to the first stage
    if (!file) setStage(1);

    // Separate the header from the body
    const [headerString, ...bodyList] = file;

    // Check if the string exists
    if (!headerString) setStage(1);

    // Separate the fields
    const headerFields = headerString.split(",");

    // Get the data structure and check that all of the
    // required fields are defined
    const { dataIndexes, missingRequiredFields } =
      getDataStructure(headerFields);

    // Check if there are missing required fields
    if (missingRequiredFields.length > 0) {
      setMissingFields(missingRequiredFields);
      return;
    }

    // Set the state
    setHeader(headerFields);

    // Check if there is any row
    if (bodyList && bodyList.length > 0) {
      // Transform every row into a list of strings
      const { bodyOut, errorsOut, studentsOut } = checkAndFormatRow(
        bodyList,
        dataIndexes
      );

      // And set the states
      setBody(bodyOut);
      setCsvErrors(errorsOut);
      setStudents(studentsOut as any);
    }
  }, [file]);

  /**
   * Function used to get the relevant parameters to set
   * what it's going to be created
   */
  useEffect(() => {
    if (students && students.length > 0) {
      // Get the class of each row
      const classes = students.map(
        (student: StudentImportListType) => student.classes[0]
      );

      // Get the unrepeated different classes
      const uniqueClasses = [...new Set(classes)];

      // Set the classes
      setClasses(uniqueClasses);
      // And the number of classes
      setNumClasses(uniqueClasses.length || 0);
    }
  }, [students]);

  /**
   * Defines the data structure based on the values provided in the header.
   *
   * @param headerFields - Array of header field names.
   * @returns An object containing:
   * - `dataIndexes`: A mapping of expected field names to their index in the header array,
   *    or `null` if the field is not present.
   *    For example, given the header ["First name", "Last name", "Gender"], the result might be:
   *    {
   *      firstName: 0,
   *      lastName: 1,
   *      dateOfBirth: null,
   *      gender: 2
   *    }
   * - `missingRequiredFields`: An object indicating which required fields were not found.
   */
  const getDataStructure = (headerFields: string[]) => {
    // Define a new version for the data structure
    const dataIndexes: { [key: string]: number } = {};
    const missingRequiredFields: string[] = [];

    // For each one of this parameters
    for (const field in DATA_STRUCTURE) {
      // Get the header reference
      const { name, required, headerReferences } = DATA_STRUCTURE[field];

      // Find the index of this field in the header
      const index = headerFields.findIndex((item: string) =>
        headerReferences.includes(item.toLowerCase())
      );

      // If the index was found, then add the index to the data structure
      if (index !== -1) dataIndexes[field] = index;
      // Check if the missing parameter is required
      else if (required) missingRequiredFields.push(name);
    }

    return { dataIndexes, missingRequiredFields };
  };

  /**
   * Function used to check the format of each element in the row
   * @param bodyList
   * @param dataIndexes
   * @returns
   */
  const checkAndFormatRow = (
    bodyList: string[],
    dataIndexes: {
      [key: string]: number;
    }
  ) => {
    // Define the output lists
    const bodyOut: string[][] = [];
    const errorsOut: string[][] = [];
    const studentsOut: { [key: string]: any }[] = [];

    // Transform every row into a list of strings
    for (const row of bodyList) {
      // Define the student info from this row
      const studentInfo: { [key: string]: any } = {};
      // Define the possible error messages just in this row
      const errorsInRow: string[] = [];

      // Check that the row is not empty
      if (!row) continue;

      // Get the elements of the row as an array
      let rowArray = row.split(",");

      // Get the expected number of columns based on the defined structure
      const expectedCols = Object.keys(dataIndexes).length;

      // First of all, padding the row to fit the number of elements in the header
      if (rowArray.length < expectedCols) {
        // Define the array to pad
        const paddingArray = Array(expectedCols - rowArray.length).fill("");
        // And extend with the padding array
        rowArray = [...rowArray, ...paddingArray];
      }

      // Go one by one reviewing the data structure
      for (const property in dataIndexes) {
        // Get the index were this property is located on each row
        const index = dataIndexes[property];

        // Get the property structure
        const structure = DATA_STRUCTURE[property];

        // Apply the criterion based on the property
        // First of all, check if the field is required
        if (structure.required) {
          // Check if it's defined in the row
          if (!rowArray[index]) {
            // Add a comment, send this row to the errors list and go to the next row
            errorsInRow.push(`${structure.name} is undefined`);
            continue;
          }
        }

        // Check if the current value is defined to process the value
        // and add it to the record
        if (!rowArray[index]) continue;

        // Apply custom processing criterion based on the property
        if (property === "dateOfBirth") {
          // Check if the date is defined and has the appropriate format
          if (!dateDDMMYYYYRegex.test(rowArray[index])) {
            // Add a comment, send this row to the errors list and go to the next row
            errorsInRow.push("The Date of Birth is incorrectly formatted");
            continue;
          } else {
            // Format the date of birth
            rowArray[index] = rowArray[index].replace(/-/g, "/");
          }
        } else if (property === "gender") {
          // Format the gender
          rowArray[index] = rowArray[index]
            ? capitaliseFirstChar(rowArray[index])
            : "";

          // Check if the gender is defined and has the appropriate format
          if (!genderList.includes(rowArray[index])) {
            // Add a comment, send this row to the errors list and go to the next row
            errorsInRow.push("The Gender is incorrectly formatted");
            continue;
          }
        } else if (property === "heritage") {
          if (!["Y", "N"].includes(rowArray[index])) {
            // Add a comment, send this row to the errors list and go to the next row
            errorsInRow.push("Heritage field is incorrectly formatted");
            continue;
          }
        } else if (property === "username") {
          // And leave trimmed and lowercased the username
          rowArray[index] = rowArray[index].trim().toLowerCase();
        }

        // If everything went right, then add the info to the student object
        studentInfo[property] = ["classes", "notes"].includes(property)
          ? [rowArray[index]] // Add as an array in these cases
          : rowArray[index];
      }

      // Check if there are errors to include
      if (errorsInRow.length > 0) {
        // Transform into a string
        const errorMsg = errorsInRow.join(",");
        // And add the error into the errors to be send
        errorsOut.push([...rowArray, errorMsg]);
        continue;
      }

      // If everything passes, add the row  to the body
      bodyOut.push(rowArray);

      // Format the date of birth
      if (studentInfo.dateOfBirth) {
        const [day, month, year] = studentInfo.dateOfBirth
          .split("/")
          .map(Number);

        // Replace the dateofBirth
        studentInfo.dateOfBirth = new Date(year, month - 1, day);
      }

      // Format the heritage
      studentInfo.heritage = studentInfo.heritage === "Y";

      // Default the gender to "" if not defined
      studentInfo.gender = studentInfo.gender || "";

      studentsOut.push(studentInfo);
    }

    return { bodyOut, errorsOut, studentsOut };
  };

  /**
   * Function executed when pressing the "replace" option on the warning
   * sub view of the "Check format" step
   */
  const handleReplaceRecords = () => {
    // Define the import function
    setImportOption("replace");

    // And go to stage 3
    setStage(3);
  };

  /**
   * Function executed when pressing the "update" option on the warning
   * sub view of the "Check format" step
   */
  const handleUpdateRecords = () => {
    // Define the import function
    setImportOption("update");

    // And go to stage 3
    setStage(3);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.1, ease: "easeOut" }}
      className="self-stretch w-[437px] flex-col justify-start items-start gap-8 inline-flex overflow-y-auto overflow-x-hidden custom-scroll"
    >
      <div className="h-[590px] max-h-[590px] flex-col justify-start items-start gap-8 inline-flex">
        <AnimatePresence mode="wait">
          {checkFormatState === "import" ? (
            <>
              {missingFields && missingFields.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.1, ease: "easeOut" }}
                  className="self-stretch h-fit flex-col justify-start items-start gap-8 inline-flex overflow-y-auto custom-scroll"
                >
                  <div className="self-stretch text-neutral-700 text-sm font-semibold font-sans leading-[17px]">
                    We noticed some missing fields
                  </div>
                  <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                    <div className="self-stretch text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                      The following required fields are missing in the header
                      definition:
                    </div>
                    <div className="self-stretch h-fit justify-start items-center gap-1 inline-flex">
                      {missingFields.map((item: string, index: number) => {
                        return (
                          <React.Fragment key={index}>
                            <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                            <div className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                              {item}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="self-stretch text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                      Please include the missing fields in the CSV file, and try
                      to upload it again.
                    </div>
                  </div>
                </motion.div>
              ) : (
                <>
                  <div className="self-stretch justify-start items-center gap-0.5 inline-flex">
                    <div className="grow shrink basis-0">
                      <span className="text-neutral-700 text-sm font-bold font-sans leading-[17px]">
                        {body ? body.length : 0} students
                      </span>
                      <span className="text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                        {" "}
                        from{" "}
                      </span>
                      <span className="text-neutral-700 text-sm font-bold font-sans leading-[17px]">
                        {numClasses} classes
                      </span>
                      <span className="text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                        {" "}
                        have been identified.
                      </span>
                    </div>
                  </div>
                  <div className="self-stretch justify-start items-center gap-0.5 inline-flex">
                    <div className="grow shrink basis-0 text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                      Please review the CSV format before confirming import:
                    </div>
                  </div>
                  <div className="w-full text-[10px] font-sans border rounded-md overflow-y-auto custom-scroll">
                    <table className="w-full table-auto border">
                      <thead className="sticky top-0">
                        <tr className="bg-neutral-600">
                          {header &&
                            header.map((field: string, index: number) => (
                              <th
                                key={index}
                                className="text-left text-white px-2 border-x"
                              >
                                {field}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {body &&
                          body.length > 0 &&
                          body.map((row: string[], index: number) => (
                            <tr key={index} className="border border-dashed">
                              {row &&
                                row.length > 0 &&
                                row.map((value: string, indexValue: number) => (
                                  <td
                                    key={indexValue}
                                    className="px-2 border-x"
                                  >
                                    {value}
                                  </td>
                                ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          ) : checkFormatState === "processing" ? (
            <ProcessingDataAnimation
              finishedValidation={false}
              processingText="Importing data"
            />
          ) : checkFormatState === "warning" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              className="self-stretch h-fit flex-col justify-start items-start gap-8 inline-flex overflow-y-auto custom-scroll"
            >
              <div className="self-stretch text-neutral-700 text-sm font-semibold font-sans leading-[17px]">
                We noticed some conflicts
              </div>
              <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                  We noticed that some of the students and classes you are
                  trying to import already exist.
                </div>
                <div className="self-stretch text-neutral-700 text-sm font-sans leading-[17px]">
                  <span className="font-normal ">We can </span>
                  <span className="font-bold">replace</span>
                  <span className="font-normal">
                    {" "}
                    existing classes with the same name, but this would remove
                    all existing students within that class and replace them
                    with students from the CSV file.
                  </span>
                </div>
                <div className="self-stretch text-neutral-700 text-sm font-sans leading-[17px]">
                  <span className="font-normal">Alternatively, we can </span>
                  <span className="font-bold">update</span>
                  <span className="font-normal">
                    {" "}
                    students by adding any missing data available in the file to
                    the existing user information.
                  </span>
                </div>
                <div className="self-stretch text-neutral-700 text-sm font-bold font-sans leading-[17px]">
                  Existing students will continue to use the same login details.
                </div>
                <div className="self-stretch text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                  How would you like to proceed?
                </div>
              </div>
              <div className="self-stretch h-fit flex-col justify-start items-start gap-4 flex">
                <ConflictButton
                  title="Replace students & classes"
                  subTitle="Replace existing students & classes with those in your file"
                  handleFunction={handleReplaceRecords}
                />
                <ConflictButton
                  title="Update students & classes"
                  subTitle="Adding students to existing classes or classes to existing students"
                  handleFunction={handleUpdateRecords}
                />
                <ConflictButton
                  title="Upload new file"
                  subTitle="Return to previous step and upload new file"
                  handleFunction={comeBackToStage1}
                />
              </div>
            </motion.div>
          ) : (
            <></>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CheckFormat;
